/* Admin tab , view & manage $1,000 holding deposits paid by customers. */
import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, Calendar, Phone, Mail, ChevronDown, Check } from "lucide-react";
import { adminApi } from "./adminApi";

const STATUS_OPTIONS = [
 { id: "held", label: "Held", color: "bg-amber-100 text-amber-900 ring-amber-300" },
 { id: "confirmed", label: "Confirmed", color: "bg-sky-100 text-sky-900 ring-sky-300" },
 { id: "applied", label: "Applied to invoice", color: "bg-emerald-100 text-emerald-900 ring-emerald-300" },
 { id: "refunded", label: "Refunded", color: "bg-slate-100 text-slate-700 ring-slate-300" },
 { id: "cancelled", label: "Cancelled", color: "bg-rose-100 text-rose-900 ring-rose-300" },
];

export default function AdminHoldingDeposits() {
 const [items, setItems] = useState(null);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState("");

 const load = async () => {
  setLoading(true);
  setError("");
  try {
   const data = await adminApi("/admin/holding-deposits");
   setItems(data.deposits || []);
  } catch (e) {
   setError(e.message);
  } finally {
   setLoading(false);
  }
 };

 useEffect(() => { load(); }, []);

 const updateStatus = async (id, status) => {
  try {
   await adminApi(`/admin/holding-deposits/${id}/status`, { method: "PATCH", body: { status } });
   setItems(prev => prev.map(d => d.id === id ? { ...d, status } : d));
  } catch (e) {
   setError(e.message);
  }
 };

 if (!items) return <div className="text-slate-500 text-sm py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading deposits…</div>;

 return (
  <div className="space-y-5" data-testid="admin-holding-deposits">
   <div className="flex items-center justify-between">
    <div>
     <h2 className="text-2xl font-black text-slate-900 tracking-tight">Holding Deposits</h2>
     <p className="text-slate-600 text-sm mt-1">$1,000 deposits paid by customers to reserve a date for private charters.</p>
    </div>
    <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-bold disabled:opacity-50" data-testid="deposits-refresh">
     {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
    </button>
   </div>

   {error && <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 text-rose-900 text-sm px-4 py-3">{error}</div>}

   {items.length === 0 ? (
    <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-6 py-12 text-center text-slate-500 text-sm">
     No deposits yet. When a customer pays a $1,000 holding deposit on a private charter page, it will appear here.
    </div>
   ) : (
    <div className="space-y-3" data-testid="deposits-list">
     {items.map(d => {
      const statusOpt = STATUS_OPTIONS.find(s => s.id === d.status) || STATUS_OPTIONS[0];
      return (
       <div key={d.id} className="rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 transition-colors shadow-sm" data-testid={`deposit-row-${d.id}`}>
        <div className="px-5 py-4 flex flex-wrap items-start gap-4">
         <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
           <div className="font-black text-slate-900">{d.customer_name}</div>
           <span className={`text-[10px] uppercase tracking-[0.22em] font-black px-2.5 py-0.5 rounded-full ring-1 ${statusOpt.color}`}>{statusOpt.label}</span>
           <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{d.cruise_label || d.cruise_type}</span>
          </div>
          <div className="text-sm text-slate-600 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
           <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> <strong>{formatDate(d.requested_date)}</strong></span>
           <a href={`mailto:${d.customer_email}`} className="inline-flex items-center gap-1.5 hover:text-slate-900"><Mail className="w-3.5 h-3.5" />{d.customer_email}</a>
           {d.customer_phone && <a href={`tel:${d.customer_phone}`} className="inline-flex items-center gap-1.5 hover:text-slate-900"><Phone className="w-3.5 h-3.5" />{d.customer_phone}</a>}
           {d.guests && <span>Approx. <strong>{d.guests}</strong> guests</span>}
          </div>
          {d.notes && <div className="text-sm text-slate-500 mt-1.5 italic">"{d.notes}"</div>}
         </div>
         <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-[0.28em] font-black text-slate-500">Paid</div>
          <div className="text-2xl font-black text-slate-900 tabular-nums">${(d.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">incl. ${(d.surcharge || 0).toFixed(2)} card fee</div>
         </div>
        </div>
        <div className="px-5 pb-4 flex items-center gap-2 flex-wrap">
         {STATUS_OPTIONS.map(s => (
          <button key={s.id} onClick={() => updateStatus(d.id, s.id)} disabled={d.status === s.id} className={`text-[10px] uppercase tracking-[0.22em] font-black px-3 py-1.5 rounded-full ring-1 transition-all ${d.status === s.id ? `${s.color} ring-2` : "bg-white ring-slate-200 text-slate-500 hover:bg-slate-50"}`} data-testid={`deposit-${d.id}-status-${s.id}`}>
           {d.status === s.id && <Check className="w-3 h-3 inline mr-1" />}{s.label}
          </button>
         ))}
        </div>
       </div>
      );
     })}
    </div>
   )}
  </div>
 );
}

function formatDate(iso) {
 if (!iso) return "";
 const d = new Date(iso + "T00:00:00");
 return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}
